"use client";

import { createElement, lazy } from "react";
import type { ToolUIRegistry, ToolView } from "../../tool-renderer";

/**
 * Views for the federated knowledge index (ADR-111).
 *
 * The legacy Knowledge Engine registry keys its search `legacyKnowledgeSearch`,
 * so the two never collide. Chats persisted before that rename still carry
 * Knowledge Engine output under `knowledgeSearch`, and those go to the legacy
 * view — loaded only when one turns up.
 */
const LegacySearchView = lazy(() =>
  import("../knowledge-engine/search").then(
    ({ LegacyKnowledgeSearchToolView }) => ({
      default: LegacyKnowledgeSearchToolView as ToolView,
    })
  )
);

export const knowledgeToolRegistry = {
  knowledgeSearch: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./search").then(
        ({ isGraphSearchOutput, KnowledgeSearchToolView }) => {
          const SearchView: ToolView = ({ invocation }) =>
            createElement(
              isGraphSearchOutput(invocation.output)
                ? LegacySearchView
                : KnowledgeSearchToolView,
              { invocation }
            );
          return { default: SearchView };
        }
      )
    ),
  },
  // Traversal — reading the intranet itself rather than the index (§10).
  knowledgeListSpaces: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./browse").then(({ KnowledgeListSpacesToolView }) => ({
        default: KnowledgeListSpacesToolView,
      }))
    ),
  },
  knowledgeBrowseSpace: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./browse").then(({ KnowledgeBrowseSpaceToolView }) => ({
        default: KnowledgeBrowseSpaceToolView,
      }))
    ),
  },
  knowledgeReadPage: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./browse").then(({ KnowledgeReadPageToolView }) => ({
        default: KnowledgeReadPageToolView,
      }))
    ),
  },
  knowledgeGetNavChildren: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./browse").then(({ KnowledgeGetNavChildrenToolView }) => ({
        default: KnowledgeGetNavChildrenToolView,
      }))
    ),
  },
  knowledgeGetMedia: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./browse").then(({ KnowledgeGetMediaToolView }) => ({
        default: KnowledgeGetMediaToolView,
      }))
    ),
  },
  openKnowledgePage: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./open-page").then(({ OpenKnowledgePageToolView }) => ({
        default: OpenKnowledgePageToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;
